import { ChevronLeft, ChevronRight } from "lucide-react";
import * as React from "react";
import { cn } from "../../lib/utils";

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  appointmentsCount?: number;
  totalCount?: number;
  searchQuery?: string;
  statusFilter?: string;
  dateFilter?: string;
  className?: string;
}

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
  appointmentsCount,
  totalCount,
  searchQuery = "",
  statusFilter = "Upcoming",
  dateFilter = "All Time",
  className = "",
}) => {
  if (totalPages <= 1 && !appointmentsCount) return null;

  const isFiltered =
    (searchQuery && searchQuery.trim()) ||
    (statusFilter && statusFilter !== "Upcoming") ||
    (dateFilter && dateFilter !== "All Time");

  // Helper to generate page numbers (with ellipsis if needed)
  const getPageNumbers = () => {
    const pages = [];
    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (currentPage <= 3) {
        pages.push(1, 2, 3, 4, "...", totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(
          1,
          "...",
          totalPages - 3,
          totalPages - 2,
          totalPages - 1,
          totalPages,
        );
      } else {
        pages.push(
          1,
          "...",
          currentPage - 1,
          currentPage,
          currentPage + 1,
          "...",
          totalPages,
        );
      }
    }
    return pages;
  };

  return (
    <div className={cn("w-full flex flex-col items-end", className)}>
      {/* Info text */}
      {typeof appointmentsCount === "number" &&
        typeof totalCount === "number" && (
          <div className="text-sm text-gray-600 mb-2 w-full text-left">
            Showing {appointmentsCount} of {totalCount}
            {isFiltered && " (filtered)"}
          </div>
        )}
      {/* Pagination bar */}
      {totalPages > 1 && (
        <div
          className="flex items-center justify-end gap-0 rounded-2xl border border-[#E7E8E9] overflow-hidden bg-white"
          style={{ width: "fit-content", marginLeft: "auto" }}
        >
          {/* Previous Arrow */}
          <button
            className="px-4 py-2 text-gray-500 hover:bg-gray-100 disabled:text-gray-300 disabled:cursor-not-allowed focus:outline-none border-r border-[#E7E8E9]"
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
            aria-label="Previous page"
            type="button"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>

          {/* Page Numbers */}
          {getPageNumbers().map((page, idx, arr) =>
            page === "..." ? (
              <span
                key={"ellipsis-" + idx}
                className="px-2 py-2 text-gray-400 select-none text-lg border-r border-[#E7E8E9] last:border-r-0"
              >
                ...
              </span>
            ) : (
              <button
                key={page}
                className={`px-5 py-2 text-lg font-medium border-0 bg-transparent focus:outline-none transition-colors border-r border-[#E7E8E9] last:border-r-0
                  ${currentPage === page ? "text-[#0052FD] font-bold" : "text-black"}`}
                onClick={() => onPageChange(Number(page))}
                aria-current={currentPage === page ? "page" : undefined}
                type="button"
                style={{
                  borderRight:
                    idx === arr.length - 1 ? "none" : "1px solid #E7E8E9",
                  borderLeft: "none",
                  borderTop: "none",
                  borderBottom: "none",
                  background: "transparent",
                }}
              >
                {page}
              </button>
            ),
          )}

          {/* Next Arrow */}
          <button
            className="px-4 py-2 focus:outline-none border-l border-[#E7E8E9]"
            style={{
              color: currentPage === totalPages ? "#D1D5DB" : "#2563EB",
              cursor: currentPage === totalPages ? "not-allowed" : "pointer",
              background: "transparent",
            }}
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            aria-label="Next page"
            type="button"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      )}
    </div>
  );
};

export default Pagination;
